import React, { useRef, useEffect } from 'react';
import ChatMessage from './ChatMessage';

/**
 * ChatMessages - Scrollable message list component
 *
 * Displays all messages with auto-scroll to bottom
 * Shows loading indicator and error states
 */
const ChatMessages = ({ messages, isLoading, error }) => {
  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  return (
    <div
      style={{
        flex: 1,
        overflowY: 'auto',
        padding: '16px',
        backgroundColor: '#f9f9f9',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Empty state */}
      {messages.length === 0 && !isLoading && !error && (
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            color: '#999',
            padding: '40px 20px'
          }}
        >
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>💬</div>
          <div style={{ fontSize: '14px' }}>Loading your assistant...</div>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div
          style={{
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '8px',
            padding: '12px',
            marginBottom: '12px',
            color: '#856404',
            fontSize: '13px'
          }}
        >
          ⚠️ {error}
        </div>
      )}

      {/* Messages */}
      {messages.map((msg) => (
        <ChatMessage key={msg.id} message={msg} />
      ))}

      {/* Loading indicator */}
      {isLoading && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
            marginBottom: '12px'
          }}
        >
          <div
            style={{
              backgroundColor: '#f0f0f0',
              borderRadius: '18px 18px 18px 4px',
              padding: '12px 16px',
              maxWidth: '80%'
            }}
          >
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <span className="typing-dot" style={{ animation: 'typing 1.4s infinite' }}>●</span>
              <span className="typing-dot" style={{ animation: 'typing 1.4s infinite 0.2s' }}>●</span>
              <span className="typing-dot" style={{ animation: 'typing 1.4s infinite 0.4s' }}>●</span>
            </div>
          </div>
        </div>
      )}

      {/* Animations for typing dots and streaming cursor */}
      <style>
        {`
          @keyframes typing {
            0%, 60%, 100% {
              opacity: 0.3;
            }
            30% {
              opacity: 1;
            }
          }
          @keyframes mikeCursorBlink {
            0%, 100% { opacity: 1; }
            50% { opacity: 0; }
          }
        `}
      </style>

      {/* Invisible div for auto-scroll */}
      <div ref={messagesEndRef} />
    </div>
  );
};

export default ChatMessages;
